import CompactBrief from '@components/CompactBrief';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import { useProfileContext } from '@components/Contexts/ProfileContext';
import { useTemplatesContext } from '@components/Contexts/TemplatesContext';
import ErrorFallback from '@components/ErrorFallback';
import Header from '@components/Header';
import useAccountCertifications from '@components/Hooks/useAccountCertifications';
import { useBi } from '@components/Hooks/useBi';
import { useCopyToClipboard } from '@components/Hooks/useCopyToClipboard';
import useMobileMediaQuery from '@components/Hooks/useMobileMediaQuery';
import { useProfilePageBi } from '@components/Hooks/useProfilePageBi';
import ContactStatusToast from '@components/Profile_New/ContactStatusToast';
import ProfileTemplates from '@components/Profile_New/ProfileTemplates/ProfileTemplates';
import ProjectsReviewsWithPaging from '@components/Profile_New/ProjectsReviews/ProjectsReviewsWithPaging';
import SectionWrapper from '@components/Profile_New/SectionWrapper';
import {
  marketplaceBriefGeneralAction,
  partnersProfilePagePageLoaded,
} from '@wix/bi-logger-marketplace/v2';
import { Box } from '@wix/design-system';
import { useIntersect } from '@wix/experts-common';
import { useErrorMonitor } from '@wix/fe-essentials-standalone';
import CopyNotification from 'components/Profile_New/CopyNotification';
import _ from 'lodash';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { animateScroll } from 'react-scroll';
import About from './About';
import BackToExplorer from './BackToExplorer';
import ContactPanel from './ContactPanel';
import FeaturedProjects from './FeaturedProjects';
import MiniContactPanel from './MiniContactPanel';
import NavBar from './NavBar';
import { Sections } from './NavBar/NavBar';
import s from './Profile.module.scss';
import StartingRates from './StartingRates';
import TraceableTraderData from './TraceableTraderData';

const Profile: FC = () => {
  const logger = useBi();
  const { sessionId } = useBiSessionContext();
  const { userDetails } = useHeaderContext();
  const {
    partnerProfile,
    entry,
    origin,
    appStatus,
    enabled,
    isRenderedInModal,
    isMobileUserAgent,
    host,
  } = useProfileContext();
  const isMobileView = useMobileMediaQuery(isMobileUserAgent);
  const {
    state: { numberOfResults: numberOfTemplates },
  } = useTemplatesContext();
  const { accountCertificationsState } = useAccountCertifications();
  const certifications = accountCertificationsState.data ?? [];
  const errorMonitor = useErrorMonitor();
  const [isOpen, setIsOpen] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [ref, entryElement] = useIntersect({}) as any;
  const [showShadow, setShowShadow] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(false);
  const [briefTimedOutMessage, setBriefTimedOutMessage] = useState(false);
  const [copyStatus, setCopyStatus] = useState<null | 'success' | 'error'>(
    null,
  );

  const [activeTab, setActiveTab] = useState(Sections.ABOUT);
  const profileUrl = `${host}/marketplace/wix-partner/${partnerProfile.slug}`;

  const { copy } = useCopyToClipboard({
    onSuccess: () => setCopyStatus('success'),
    onError: () => setCopyStatus('error'),
    onClear: () => setCopyStatus(null),
  });
  const { logProfilePageClick } = useProfilePageBi();

  const navigationRefs = useRef<any>({});

  useEffect(() => {
    if (entryElement.intersectionRatio < 1) {
      setShowShadow(true);
    } else {
      setShowShadow(false);
    }
  }, [entryElement]);

  const handleHashChange = useCallback(() => {
    if (_.includes(window.location.hash, '#brief')) {
      setIsOpen(enabled);
    } else {
      setIsOpen(false);
    }
  }, [userDetails, enabled]);

  useEffect(() => {
    window.addEventListener('popstate', handleHashChange);
    if (_.includes(window.location.hash, '#brief') && enabled) {
      handleHashChange();
    }
    return () => {
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [handleHashChange]);

  function closeModal(exitType?: string) {
    if (exitType !== 'submit') {
      logger.report(
        marketplaceBriefGeneralAction({
          briefType: 'compact',
          action_type: 'exit/cancel',
          sessionId,
          origin,
          entry,
        }),
      );
    }
    history.replaceState(history.state, '', '#');
    setIsOpen(false);
    setBriefTimedOutMessage(false);
  }

  useEffect(() => {
    try {
      if (location.hash) {
        const sectionId = location.hash.slice(1);
        if (document && document.getElementById(sectionId)) {
          const offset =
            document.getElementById(sectionId)!.getBoundingClientRect().top +
            105;
          animateScroll.scrollMore(offset, {
            duration: 1500,
            delay: 100,
            smooth: true,
          });
        }
      }
    } catch (err) {
      errorMonitor.captureException(err as Error);
    }
  }, [errorMonitor]);

  const [showBack, setShowBack] = useState(false);
  useEffect(() => {
    setShowBack(document.referrer.indexOf('hire') > -1);
  }, []);

  const onCopyClicked = () => {
    logProfilePageClick('share');
  };

  function onContactClicked() {
    logProfilePageClick('connect');
    history.replaceState(history.state, '', '#brief');
    handleHashChange();
  }

  function headerOnClick() {
    logProfilePageClick('marketplace top button');
  }

  function scrollToReviewsSection() {
    if (navigationRefs.current) {
      const offset = isMobileView ? 130 : 100;
      const targetPosition =
        navigationRefs.current[
          Sections.SERVICES_AND_REVIEWS
        ].getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: targetPosition - offset,
        behavior: 'smooth',
      });
    }
  }

  const handleMobileTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  // Scroll when tab changes in mobile view
  useEffect(() => {
    if (isMobileView && activeTab) {
      window.scrollTo({
        top: showShadow ? 350 : 0,
        behavior: 'smooth',
      });
    }
  }, [activeTab, isMobileView, showShadow]);

  useEffect(() => {
    if (appStatus === 'initialized' && !isFirstLoad) {
      logger.report(
        partnersProfilePagePageLoaded({
          sessionId,
          origin: origin || document.referrer,
          entry,
          exploreVersion: 'V2',
          provider_id: partnerProfile.id,
        }),
      );
      setIsFirstLoad(true);
    }
  }, [appStatus, entry, isFirstLoad, origin, partnerProfile.id, sessionId]);

  const contactStatus = showSuccessNotification
    ? 'success'
    : showErrorNotification
    ? 'error'
    : null;

  const clearStatusMap: Record<'success' | 'error', () => void> = {
    success: () => setShowSuccessNotification(false),
    error: () => setShowErrorNotification(false),
  };
  if (isMobileView) {
    return (
      <Box
        display="block"
        dataHook="mobile-profile-page"
        className={s.mobileContainer}
      >
        {!isRenderedInModal ? (
          <Header
            onClick={headerOnClick}
            pageName="profile page"
            sessionId={sessionId}
          />
        ) : null}
        <ContactStatusToast
          status={contactStatus}
          clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
          isMobileView={isMobileView}
        />
        <CopyNotification
          status={copyStatus}
          clearStatus={() => setCopyStatus(null)}
          isMobileView={true}
        />
        <BackToExplorer showBack={showBack} isMobileView={true} />
        <Box paddingTop={showBack ? '0px' : isMobileView ? '30px' : '24px'}>
          <ContactPanel
            isMobileView={true}
            onContactClicked={onContactClicked}
            copy={copy}
            onCopyClicked={onCopyClicked}
            shareUrl={profileUrl}
            certifications={certifications}
          />
        </Box>
        <div
          ref={ref}
          className={
            showShadow ? `${s.stickyHeader} ${s.showShadow}` : s.stickyHeader
          }
        >
          <MiniContactPanel
            onContactClicked={onContactClicked}
            copy={copy}
            onCopyClicked={onCopyClicked}
            shareUrl={profileUrl}
          />
        </div>
        <NavBar
          isMobileView={true}
          navigationRefs={navigationRefs}
          onTabChange={handleMobileTabChange}
          activeTab={activeTab}
        />
        <Box
          dataHook="profile-page-sections"
          className={`${s.profilePageSections} ${s.mobile}`}
          direction="vertical"
        >
          {activeTab === Sections.ABOUT && (
            <SectionWrapper sectionId={Sections.ABOUT} ref={navigationRefs}>
              <About
                isMobileView={isMobileView}
                certifications={certifications}
              />
            </SectionWrapper>
          )}
          {activeTab === Sections.CLIENT_PROJECTS && (
            <SectionWrapper
              sectionId={Sections.CLIENT_PROJECTS}
              ref={navigationRefs}
            >
              <FeaturedProjects isMobileView={true} />
            </SectionWrapper>
          )}
          {activeTab === Sections.SERVICES_AND_REVIEWS && (
            <SectionWrapper
              sectionId={Sections.SERVICES_AND_REVIEWS}
              ref={navigationRefs}
            >
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <StartingRates isMobileView={true} />

                <ProjectsReviewsWithPaging
                  isMobileView={isMobileView}
                  onPageChange={scrollToReviewsSection}
                />
              </ErrorBoundary>
            </SectionWrapper>
          )}
          {activeTab === Sections.TEMPLATES && numberOfTemplates > 0 && (
            <SectionWrapper sectionId={Sections.TEMPLATES} ref={navigationRefs}>
              <ProfileTemplates isMobileView={true} />
            </SectionWrapper>
          )}
        </Box>
        <CompactBrief
          isMobileView={true}
          closeModal={closeModal}
          isOpen={isOpen}
          setShowSuccessNotification={setShowSuccessNotification}
          setShowErrorNotification={setShowErrorNotification}
          setBriefTimedOutMessage={setBriefTimedOutMessage}
          briefTimedOutMessage={briefTimedOutMessage}
        />
      </Box>
    );
  }
  return (
    <Box display="block" dataHook="profile-page" backgroundColor="white">
      {!isRenderedInModal ? (
        <div className={s.stickyHeader}>
          <Header
            onClick={headerOnClick}
            pageName="profile page"
            sessionId={sessionId}
          />
        </div>
      ) : (
        <div className={s.stickyHeader}></div>
      )}
      <CopyNotification
        status={copyStatus}
        clearStatus={() => setCopyStatus(null)}
        isMobileView={false}
      />
      <ContactStatusToast
        status={contactStatus}
        clearStatus={contactStatus ? clearStatusMap[contactStatus] : () => {}}
        isMobileView={isMobileView}
      />
      <Box
        dataHook="profile-page-container"
        className={s.profilePageContainer}
        direction="horizontal"
      >
        <Box className={s.leftPanel} dataHook="left-panel" direction="vertical">
          <BackToExplorer showBack={showBack} isMobileView={false} />
          <ContactPanel
            isMobileView={false}
            onContactClicked={onContactClicked}
            copy={copy}
            onCopyClicked={onCopyClicked}
            shareUrl={profileUrl}
            certifications={certifications}
          />
        </Box>

        <Box direction="vertical" width="100%">
          <NavBar
            isMobileView={false}
            navigationRefs={navigationRefs}
            showBack={showBack}
          />
          <Box
            dataHook="profile-page-sections"
            className={s.profilePageSections}
            direction="horizontal"
          >
            <div style={{ width: '100%' }}>
              <SectionWrapper ref={navigationRefs} sectionId={Sections.ABOUT}>
                <About
                  isMobileView={isMobileView}
                  certifications={certifications}
                />
              </SectionWrapper>

              <SectionWrapper
                sectionId={Sections.CLIENT_PROJECTS}
                ref={navigationRefs}
              >
                <FeaturedProjects isMobileView={false} />
              </SectionWrapper>
              <SectionWrapper
                sectionId={Sections.SERVICES_AND_REVIEWS}
                ref={navigationRefs}
              >
                <Box paddingBottom="35px">
                  <StartingRates />
                </Box>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                  <ProjectsReviewsWithPaging
                    isMobileView={isMobileView}
                    onPageChange={scrollToReviewsSection}
                  />
                </ErrorBoundary>
              </SectionWrapper>
              {numberOfTemplates > 0 && (
                <SectionWrapper
                  sectionId={Sections.TEMPLATES}
                  ref={navigationRefs}
                >
                  <ProfileTemplates isMobileView={isMobileView} />
                </SectionWrapper>
              )}
              <TraceableTraderData />
            </div>
          </Box>
        </Box>
      </Box>
      <CompactBrief
        isMobileView={false}
        closeModal={closeModal}
        isOpen={isOpen}
        setShowSuccessNotification={setShowSuccessNotification}
        setShowErrorNotification={setShowErrorNotification}
        setBriefTimedOutMessage={setBriefTimedOutMessage}
        briefTimedOutMessage={briefTimedOutMessage}
      />
    </Box>
  );
};

export default Profile;
